import type { CategoryContent } from './row.js';

export const ASTRONOMY: CategoryContent = {
  bscba: [
    [
      'What does parallax measure in astronomy?',
      [
        'The apparent shift in a star\'s position',
        'The brightness increase of a star due to gravitational lensing',
        'The rotation period of a distant galaxy',
        'The spectral shift caused by stellar atmospheric movement',
      ],
      0,
      'Parallax is the apparent shift in nearby star positions relative to distant background stars, caused by Earth\'s orbital motion around the Sun. This baseline shift allows distance measurement.',
    ],
    [
      'On the Hertzsprung-Russell diagram, where do main sequence stars lie?',
      [
        'Along a diagonal band',
        'Clustered in the upper-right corner',
        'Randomly distributed across the plot',
        'Along the horizontal axis only',
      ],
      0,
      'Main sequence stars form a clear diagonal band on HR diagrams, reflecting the mass-luminosity relationship. This band represents stars burning hydrogen in their cores.',
    ],
    [
      'What is the spectral classification sequence for stars, from hottest to coolest?',
      [
        'OBAFGKM',
        'M, K, G, F, A, B, O',
        'A, B, F, G, K, M, O',
        'O, M, K, G, F, B, A',
      ],
      0,
      'The OBAFGKM sequence, remembered as "Oh, Be A Fine Girl, Kiss Me," orders stars by temperature and spectral features from over 25,000 K (O-type) to under 3,800 K (M-type).',
    ],
    [
      'What is the primary source of energy in the Sun?',
      [
        'Hydrogen fusion into helium',
        'Gravitational contraction of the interior',
        'Radioactive decay of heavy elements',
        'Solar wind interaction with the magnetosphere',
      ],
      0,
      'The Sun\'s core converts hydrogen to helium via the proton-proton chain reaction, releasing energy that has sustained our star for 4.6 billion years and will for about 5 billion more.',
    ],
    [
      'Which type of nebula is created by hot, ionized gas around young stars?',
      [
        'Emission nebula',
        'Reflection nebula',
        'Dark nebula',
        'Planetary nebula',
      ],
      0,
      'Emission nebulae glow from ionization by energetic radiation from nearby hot stars. They appear bright against dark space due to their own light emission in forbidden atomic lines.',
    ],
    [
      'What does the Hubble classification system categorize?',
      [
        'Galaxy morphology and structure',
        'Stellar temperature ranges',
        'Nebula composition types',
        'Exoplanet orbital characteristics',
      ],
      0,
      'Hubble classification divides galaxies into elliptical (E0–E7), spiral (Sa–Sc), and lenticular (S0) types based on visual appearance, forming the "tuning fork" diagram.',
    ],
    [
      'In the cosmic distance ladder, which method extends measurements beyond parallax?',
      [
        'Cepheid variable stars as standard candles',
        'Direct radar ranging to all galaxies',
        'Comparing apparent brightness to surface temperature',
        'Measuring magnetic field strength at distance',
      ],
      0,
      'Cepheid variables have a period-luminosity relationship, allowing absolute magnitude determination from observed pulsation periods, extending the distance scale to nearby galaxies.',
    ],
    [
      'What defines the event horizon of a black hole?',
      [
        'The boundary beyond which no object can escape',
        'The region where matter collides before falling into the singularity',
        'The visible light surface created by infalling gas',
        'The outer edge of the accretion disk around the black hole',
      ],
      0,
      'The event horizon is the point of no return defined by the escape velocity equaling light speed. No signal from inside can reach an outside observer.',
    ],
    [
      'What does cosmological redshift indicate about distant galaxies?',
      [
        'The universe is expanding, stretching light\'s wavelength',
        'Distant galaxies are moving toward us at relativistic speeds',
        'The interstellar medium absorbs shorter wavelengths preferentially',
        'Gravitational lensing bends light toward the red end of the spectrum',
      ],
      0,
      'Cosmological redshift results from space expansion stretching light wavelengths. This differs from Doppler redshift: space itself is expanding, not merely the galaxies moving through it.',
    ],
    [
      'How do astronomers detect exoplanets using the transit method?',
      [
        'Measuring the transit brightness dip',
        'Detecting reflected light from the planet\'s surface',
        'Observing the gravitational wobble of the host star',
        'Measuring infrared radiation from the planet\'s heat',
      ],
      0,
      'Transit photometry detects the slight dip in starlight when a planet passes in front of its star. This method has discovered thousands of exoplanets and allows atmosphere characterization.',
    ],
    [
      'What is the primary difference between refraction and reflection telescopes?',
      [
        'Refraction uses lenses; reflection uses mirrors',
        'Refraction can see ultraviolet; reflection is limited to visible light',
        'Reflection telescopes cannot observe distant objects beyond our galaxy',
        'Refraction telescopes are always larger than reflection telescopes',
      ],
      0,
      'Refractors use objective lenses to bend light to a focus. Reflectors use primary mirrors to focus light. Reflectors avoid chromatic aberration and allow larger apertures more economically.',
    ],
    [
      'What is angular resolution in telescopes primarily limited by?',
      [
        'Atmospheric diffraction and light wavelength',
        'The distance between the telescope and the object being viewed',
        'The temperature of the telescope\'s optical components',
        'The rate at which the Earth rotates',
      ],
      0,
      'Angular resolution follows the diffraction limit: smaller wavelength and larger aperture improve resolution. Atmospheric turbulence ("seeing") also degrades ground-based observations.',
    ],
    [
      'Which of these is an example of a binary star system?',
      [
        'Sirius A and B orbiting each other',
        'The Sun and Jupiter orbiting each other',
        'Two stars separated by several light-years showing no orbital interaction',
        'A star and its surrounding planetary system',
      ],
      0,
      'Binary stars orbit their common center of mass. Sirius A (white main sequence star) and Sirius B (white dwarf) form a wide binary with a 50-year orbital period.',
    ],
    [
      'What is the approximate mass of a neutron star compressed into Earth-sized volume?',
      [
        'About 1.4 solar masses',
        'About the same as Earth (negligible compared to stellar masses)',
        'About 10,000 solar masses',
        'About 0.01 solar masses',
      ],
      0,
      'A teaspoon of neutron star material would weigh a billion tons. The Chandrasekhar limit of 1.4 solar masses is the approximate maximum for neutron stars before collapse to black holes.',
    ],
    [
      'What historical figure first correctly explained planetary motion using mathematics?',
      [
        'Johannes Kepler\'s three laws of motion',
        'Galileo Galilei through direct telescopic observation',
        'Tycho Brahe by measuring star positions',
        'Isaac Newton through gravitational theory',
      ],
      0,
      'Kepler\'s three laws (1609–1619) described elliptical orbits, equal-area sweeping, and the period-semimajor axis relation, providing the mathematical foundation for orbital mechanics.',
    ],
  ],
  msc: [
    [
      'What does the Eddington luminosity limit define?',
      [
        'The maximum luminosity a star can sustain',
        'The brightness threshold needed to be visible from Earth without instruments',
        'The relationship between a star\'s core temperature and surface luminosity',
        'The rate at which a star loses mass through stellar wind',
      ],
      0,
      'Eddington luminosity occurs when radiation pressure balances gravity, preventing further mass loss. For a solar-mass star, this is about 38,000 solar luminosities.',
    ],
    [
      'What is the CNO cycle and when does it dominate?',
      [
        'A hydrogen fusion pathway catalyzed by carbon, nitrogen, and oxygen',
        'A process of core collapse that leads to supernova explosions in all massive stars',
        'The depletion sequence of helium to oxygen in very young stars',
        'A mechanism of energy transport through stellar convection zones',
      ],
      0,
      'The CNO cycle catalytically fuses hydrogen using C, N, and O nuclei as intermediates, becoming the dominant fusion process in massive stars where core temperatures exceed ~15 million K.',
    ],
    [
      'How does the mass-luminosity relation constrain stellar models?',
      [
        'It tests hydrostatic equilibrium and nuclear burning rates',
        'It only applies to binary star systems and cannot constrain single-star models',
        'The relation proves that all stars are chemically identical in composition',
        'It defines the maximum possible age of any main sequence star',
      ],
      0,
      'The empirical L ∝ M³ relation (roughly) emerges from hydrostatic equilibrium, opacity, and core hydrogen fusion rates. Deviations reveal stellar age, composition, or structure anomalies.',
    ],
    [
      'What is the Chandrasekhar limit and why is it significant?',
      [
        'The maximum mass (~1.4 M⊙) a white dwarf can support',
        'It is the minimum mass for a star to undergo helium fusion',
        'It defines the core density needed to initiate the CNO cycle',
        'It is the luminosity threshold separating red dwarfs from red giants',
      ],
      0,
      'Above 1.4 solar masses, electron degeneracy pressure cannot support a white dwarf\'s weight. This limit determines whether a collapsing core becomes a neutron star or black hole.',
    ],
    [
      'How does a Kerr black hole differ from a Schwarzschild black hole?',
      [
        'A Kerr metric includes spin, producing frame-dragging',
        'A Kerr black hole emits Hawking radiation; Schwarzschild ones do not',
        'Kerr black holes have no event horizon, making them observable',
        'Only Kerr black holes can accrete matter and power jets',
      ],
      0,
      'Kerr black holes rotate, adding a spin parameter a to the metric. This creates the ergosphere (where spacetime itself rotates) and reduces the event horizon radius relative to non-spinning Schwarzschild case.',
    ],
    [
      'What is the distinction between cosmological and Doppler redshift?',
      [
        'Cosmological redshift is from space expansion; Doppler from motion',
        'Cosmological redshift only affects light from the cosmic microwave background',
        'They are identical phenomena viewed at different scales',
        'Doppler redshift increases indefinitely with distance; cosmological does not',
      ],
      0,
      'Cosmological redshift stretches all wavelengths equally as space expands (z = a(t₀)/a(tₑₘᵢₜ) − 1). Doppler redshift depends on velocity component along our line of sight.',
    ],
    [
      'What does the virial theorem state about gravitationally bound systems?',
      [
        '2K + U = 0, twice kinetic plus potential energy',
        'The total energy is always positive, ensuring stars cannot collapse',
        'Potential energy and kinetic energy are always equal but opposite in sign',
        'Gravitational systems necessarily reach infinite density over time',
      ],
      0,
      'The virial theorem constrains stellar equilibrium: if gravity increases, density and temperature must rise to maintain 2K + U = 0. This governs stellar evolution and stability.',
    ],
    [
      'What is the Hertzsprung gap and what does it represent?',
      [
        'A sparsely populated region between the main sequence and giant branch',
        'A gap in spectral classification caused by overlapping temperature ranges',
        'The distance between the nearest and most distant observable stars',
        'A discontinuity in stellar color caused by atmospheric ionization transitions',
      ],
      0,
      'The Hertzsprung gap is a poorly-populated region where evolved stars spend little time: they burn hydrogen in a shell and move quickly from the main sequence to the red giant branch.',
    ],
    [
      'What characterizes the structure and evolution of asymptotic giant branch (AGB) stars?',
      [
        'Low to intermediate mass stars with inert carbon-oxygen cores, burning shells',
        'The final stable state of all stars, lasting indefinitely without further change',
        'Massive stars immediately before core collapse and supernova',
        'Stars that have exhausted all nuclear fuel and begun cooling as white dwarfs',
      ],
      0,
      'AGB stars burn hydrogen in an outer shell and helium in an intermediate shell around a C-O core. Thermal pulses from unstable helium burning cause episodic mass ejection.',
    ],
    [
      'How does gravitational lensing allow cosmological applications?',
      [
        'Massive structures bend light, magnifying distant objects for study',
        'Lensing always magnifies objects, making faint galaxies appear brighter than they truly are',
        'Lensing only occurs around black holes and has no application to nearby structures',
        'Lensing measures the chemical composition of intervening gas clouds',
      ],
      0,
      'Strong lensing creates multiple images; weak lensing subtly distorts shapes. Both probe dark matter distribution and reveal distant high-redshift galaxies invisible otherwise.',
    ],
    [
      'What is proper motion and how does it differ from parallax?',
      [
        'Proper motion is angular motion; parallax is orbital-baseline shift',
        'Proper motion applies only to nearby stars; parallax to distant ones',
        'Proper motion requires space telescopes; parallax is ground-based only',
        'They are identical measurements expressed in different units',
      ],
      0,
      'Proper motion (μ, arcsec/yr) reflects intrinsic stellar motion; parallax (p) is purely geometric (Earth\'s orbit). Distance d = 1/p parsecs requires both for space velocity.',
    ],
    [
      'What limits the accuracy of stellar parallax measurements and how was this overcome?',
      [
        'Atmospheric turbulence limits ground parallax; space telescopes reach microarcsec',
        'Parallax works only for stars within 10 light-years due to a fundamental physical law',
        'Only reflected light, not direct starlight, shows measurable parallax',
        'Interstellar dust completely obscures parallax signals for distant stars',
      ],
      0,
      'Ground-based parallax is limited to ~100 pc by seeing; Hipparcos reached ~10 pc, and Gaia now measures μarcsec precision to ~10 kpc, extending the cosmic distance ladder.',
    ],
    [
      'How is spectroscopic information used to determine stellar properties?',
      [
        'Spectral lines reveal composition, temperature, density, and velocity',
        'Spectroscopy only measures the apparent brightness of stars',
        'Spectra directly reveal a star\'s interior density without model assumptions',
        'Spectroscopy cannot distinguish between absorption and emission features',
      ],
      0,
      'Spectral absorption lines form in cool outer layers; emission lines in hot gas. Line strength depends on temperature and density; Doppler shift and broadening encode kinematics.',
    ],
    [
      'What is the period-luminosity relation for Cepheid variables and its astronomical significance?',
      [
        'Longer-period Cepheids are more luminous, allowing distance measurement',
        'The relation only applies to a small minority of stars and has limited observational utility',
        'It proves that all Cepheids have the same intrinsic brightness',
        'Period-luminosity occurs only in binary systems where orbital motion is observed',
      ],
      0,
      'Cepheids obey log P = a log M + b (approximately), making them "standard candles." Hubble used this to prove Andromeda was a galaxy, not a nebula in our galaxy.',
    ],
    [
      'What is the Tully-Fisher relation and what role does it play in cosmology?',
      [
        'Spiral galaxy luminosity correlates with rotational velocity',
        'It is a purely theoretical relation with no observational applications',
        'The relation applies equally to all galaxy types, including ellipticals',
        'It measures galaxy age by the color of its stars',
      ],
      0,
      'The Tully-Fisher relation (L ∝ v⁴ approximately) from galaxy rotation curve dynamics extends distance measurements to ~100 Mpc, complementing Cepheid and SN Ia methods.',
    ],
  ],
  phd: [
    [
      'In the Kerr metric, what is the ergosphere and what is its physical significance?',
      [
        'A region outside the event horizon enabling Penrose energy extraction',
        'A singularity at the center of black holes where density becomes infinite',
        'The region where frame-dragging reduces to negligible levels',
        'An observational artifact of Doppler shift around rotating objects',
      ],
      0,
      'In the ergosphere (between the outer and inner horizons), spacetime itself rotates with the black hole. The Penrose process can extract rotational energy, reducing spin.',
    ],
    [
      'What is the mechanism of radiatively inefficient accretion flows and what role do they play in AGN feedback?',
      [
        'Low-accretion-rate systems where jets dominate momentum feedback',
        'Extremely bright accretion states where radiation pressure dominates outflows',
        'Accretion flows where all energy is radiated in the visible spectrum',
        'Systems where accretion rates exceed the Eddington limit by orders of magnitude',
      ],
      0,
      'In radiatively inefficient accretion (RIAF), mass advects inward with low radial efficiency. Jets deposit momentum into the ICM, quenching star formation. This explains red galaxy formation.',
    ],
    [
      'What constraints do pulsar timing arrays place on gravitational wave backgrounds?',
      [
        'Timing irregularities from black hole binaries create low-frequency GW signals',
        'Pulsars emit gravitational waves directly, making them unsuitable for GW detection',
        'Only isolated supermassive black holes produce detectable GW signals',
        'Pulsar timing cannot constrain gravitational waves below frequencies of 100 Hz',
      ],
      0,
      'PTAs detect nanohertz GWs from supermassive BH mergers by timing residuals across many pulsars. Recent IPTA data show evidence for a stochastic GW background.',
    ],
    [
      'How do metallicity-dependent stellar evolution models affect the interpretation of globular cluster ages?',
      [
        'Lower metallicity clusters have different evolutionary timescales',
        'Metallicity has negligible impact on stellar evolution',
        'All globular clusters have identical metallicity regardless of formation environment',
        'Metallicity only affects stellar color, not evolutionary rates',
      ],
      0,
      'Low-[Fe/H] stars burn H slower and evolve differently; isochrones shifted. Blue horizontal branches in metal-poor clusters require careful age–[Fe/H] disentanglement.',
    ],
    [
      'What are population synthesis models and how do they constrain star formation histories?',
      [
        'PSMs combine stellar evolution tracks with IMF assumptions to predict galaxy spectra',
        'Population synthesis cannot be applied to galaxies more distant than 100 Mpc',
        'These models only work for single-burst star formation and cannot handle continuous SFR',
        'PSMs measure galaxy ages directly from photometric redshift alone',
      ],
      0,
      'PSM fitting to multi-wavelength data (UV to IR) breaks age–dust–SFR degeneracies. Bayesian inference recovers SFH from probability distributions over parameter space.',
    ],
    [
      'What are stellar oscillation modes (p, g, f) and why are they scientifically valuable?',
      [
        'Acoustic modes (p) from pressure; gravity modes (g) from buoyancy',
        'All stellar oscillations are identical and cannot distinguish internal structure',
        'Oscillation modes only occur in white dwarfs',
        'The frequency of oscillations is independent of stellar properties',
      ],
      0,
      'p-modes propagate through convective envelopes; g-modes through radiative cores. Mode frequencies (Δν, νmax) directly measure core mass, age, and mixing length from observations.',
    ],
    [
      'How does dust extinction law variation constrain dust grain properties?',
      [
        'Different extinction curves (Rᵥ) indicate varying grain size distributions',
        'Extinction is independent of dust properties and follows the same law everywhere',
        'Extinction curves only apply to nearby dust; distant dust cannot be characterized',
        'Grain size has no measurable effect on extinction wavelength dependence',
      ],
      0,
      'Rᵥ = Aᵥ / E(B−V) varies from ~2.5 (diffuse ISM) to ~5 (dense regions). Small grains cause steep UV extinction; large grains flatten it. Carbon/silicate ratios alter the UV bump.',
    ],
    [
      'How is color excess determined and how are reddening vector corrections applied in photometry?',
      [
        'E(B−V) is from intrinsic colors; correction follows the reddening vector',
        'Color excess is the difference between observed and catalog magnitudes',
        'Reddening vectors are radial lines from the star\'s intrinsic color in color-color space',
        'Color excess is independent of dust properties and can be ignored in analysis',
      ],
      0,
      'E(B−V) quantifies dust along the line of sight. The reddening vector follows the extinction curve, not a radial direction. This matters for accurate temperature/luminosity recovery.',
    ],
    [
      'What is the Hubble tension and what are leading resolutions currently under discussion?',
      [
        '~4σ discrepancy between early- and late-time H₀ measurements',
        'A minor 1σ disagreement between cosmological parameters that will be resolved by larger surveys',
        'Evidence that the universe is not expanding',
        'A disagreement only within Planck collaboration and resolved by other experiments',
      ],
      0,
      'H₀ ≈ 67 km/s/Mpc (Planck) vs. ≈73 (SH0ES). Early dark energy, evolving-w models, and large-scale structure anisotropies are active theoretical proposals.',
    ],
    [
      'How do fast radio bursts constrain cosmological parameters and test physics models?',
      [
        'FRB dispersion probes electron density and constrains H₀',
        'Fast radio bursts are solely of galactic origin with no cosmological relevance',
        'FRBs measure only the age of the universe',
        'All FRBs are explained by pulsars in our galaxy',
      ],
      0,
      'Dispersion measure DM gives electron column density; DM/z probes IGM ionization. Magnetar models for FRB origins constrain magnetic field strengths and pair plasma physics.',
    ],
    [
      'What is the current understanding of the coronal heating problem in solar physics?',
      [
        'Coronal temperature exceeds photosphere via waves and reconnection',
        'The corona is always cooler than the photosphere, contradicting older observations',
        'Coronal heating is solved by gravitational potential energy alone',
        'All theoretical models agree on a single heating mechanism',
      ],
      0,
      'The "coronal heating paradox" persists: waves and reconnection are observationally supported, but flux ropes and loop heating remain incompletely understood. Fermi and IRIS observations constrain nanoflares.',
    ],
    [
      'What are ultra-high-energy cosmic rays (UHECRs) and what origins are favored by current theory?',
      [
        'Cosmic rays >5×10¹⁹ eV; AGN jets are top candidates',
        'Cosmic rays below 10¹⁸ eV that originate from the Sun',
        'Only galactic supernovae can produce high-energy cosmic rays',
        'UHECRs are purely theoretical with no detections above 10¹⁰ eV',
      ],
      0,
      'The GZK cutoff from pion production on CMB photons limits propagation. Pierre Auger Observatory data favor extragalactic (especially AGN) origins for E > 10¹⁹ eV.',
    ],
    [
      'What do gravitational wave polarization signatures reveal about tests of modified gravity?',
      [
        'Tensor modes dominate in GR, others suppressed',
        'Gravitational waves have only one polarization state',
        'Modified gravity theories predict identical polarizations to GR',
        'Polarization measurements cannot test theories beyond GR',
      ],
      0,
      'LIGO/Virgo data from BH mergers and neutron star mergers constrain polarization content: tensor (GR) dominates; scalar/vector modes are <10% (90% CL). This rules out many modified gravity models.',
    ],
    [
      'What is reverberation mapping and how does it measure black hole masses in AGN?',
      [
        'Monitoring broad-line vs continuum lag gives M_BH',
        'Reverberation mapping only works for radio galaxies',
        'It directly observes the black hole event horizon',
        'Line lags are independent of black hole mass',
      ],
      0,
      'Time lag Δt ∝ r; velocity from Doppler width gives τ ~ GM_BH / c² equivalent. Hundreds of AGN measured this way; systematic uncertainties in inclination and f-factor remain.',
    ],
    [
      'What do the properties of quasar absorption systems reveal about IGM/CGM physics?',
      [
        'Metal/Lyman absorption in QSO spectra constrains HI',
        'Quasar absorption is entirely due to dust near the QSO host',
        'Absorption systems only exist within the host galaxy',
        'These systems have no connection to cosmological baryon cycle',
      ],
      0,
      'DLAs (log NHI > 20.3) are sites of star formation and metals. Forest clouds (NHI ~ 10¹² cm⁻²) trace large-scale structure. Metal ions (CIV, OVI) probe hot filaments and feedback.',
    ],
  ],
  professor: [
    [
      'What is the information paradox in black hole thermodynamics and what are the leading theoretical resolutions?',
      [
        'Hawking evaporation loses information; string microstates or firewalls may resolve it',
        'The information paradox has been definitively solved and no longer debated among experts',
        'Quantum mechanics must be abandoned to resolve the paradox',
        'Black holes do not evaporate and the paradox is purely hypothetical',
      ],
      0,
      'Hawking\'s calculation implies information loss, violating unitarity. String theory suggests 2^(A/4) microstates; firewall models propose Planck-scale structure at horizons. No consensus yet.',
    ],
    [
      'What are the observational and theoretical implications of modified gravity theories like MOND and f(R) gravity?',
      [
        'MOND modifies Newton\'s law at low accelerations; f(R) modifies geometry instead',
        'Modified gravity theories are experimentally ruled out and abandoned by most researchers',
        'MOND and dark matter are observationally indistinguishable at all scales',
        'Modified gravity predicts identical predictions to GR for all phenomena',
      ],
      0,
      'MOND (a₀ ≈ 10⁻¹⁰ m/s²) fits galaxy rotation curves but fails in clusters and lensing. f(R) models have same problem. CMB and strong lensing favor GR + dark matter.',
    ],
    [
      'How do large-scale structure simulations with baryon acoustic oscillations constrain dark energy?',
      [
        'BAO from galaxy clustering provides a standard ruler',
        'Large-scale structure cannot constrain dark energy models',
        'BAO measurements only apply to the local universe',
        'All dark energy models produce identical BAO predictions',
      ],
      0,
      'BAO scale from sound-horizon imprints relic oscillations in matter. DESI, BOSS, 4MOST measure BAO at multiple redshifts; combined with CMB, they constrain w(a) and alleviate Hubble tension.',
    ],
    [
      'What do intergalactic magnetic field (IGMF) constraints from TeV gamma-ray observations reveal?',
      [
        'TeV photon cascades create a faint halo constraining IGMF strength',
        'Intergalactic magnetic fields are unmeasurable and unconstrained by any observation',
        'Only ground-based radio astronomy can measure magnetic fields in intergalactic space',
        'IGMF strength is identical to galactic fields everywhere',
      ],
      0,
      'Fermi LAT and Cherenkov telescopes observe delayed cascade emission. IGMF ~0.1–1 nG inferred from non-detection of faint halos; seed field origin (dynamo, primordial) remains open.',
    ],
    [
      'What are exotic compact objects (gravastars, boson stars, dark matter cores) and current observational tests?',
      [
        'Alternatives to black holes with singularity-free interiors, tested via GW echoes',
        'All compact objects beyond neutron stars must be black holes as proven mathematically',
        'Exotic compact objects are purely speculative with no observational relevance',
        'These objects would be indistinguishable from black holes in all measurements',
      ],
      0,
      'Gravastars have stress-energy reversing in the interior; boson stars self-gravitate via scalar field. Echo signatures in GW strain post-merger are sought; tidal Love numbers constrain interior structure.',
    ],
    [
      'What do multimessenger observations from IceCube reveal about cosmic accelerators in neutrino astronomy?',
      [
        'IceCube-detected neutrinos trace hadron acceleration in jets',
        'Neutrinos are too weakly interacting to be detected from astrophysical sources',
        'Neutrino astronomy only studies solar neutrinos',
        'All neutrino events in IceCube originate from Earth\'s atmosphere',
      ],
      0,
      'IceCube detects ~100 TeV–PeV neutrinos yearly; TXS 0506+56 blazar linked to high-energy neutrino in 2017. Neutrinos escape dense regions, revealing inner jet structure.',
    ],
    [
      'What physical mechanisms drive fast radio burst emission in leading magnetar models?',
      [
        'Magnetic reconnection in magnetar magnetospheres',
        'Fast radio bursts are confirmed to be pulsars in our galaxy',
        'All FRB models require black holes at the source',
        'The emission mechanism is identical to supernova remnants',
      ],
      0,
      'Magnetar models: sudden field reconfiguration triggers Alfvén waves and magnetic reconnection, accelerating particles to produce coherent emission. Repeating FRBs (FRB 180916, SGR-like source) support this.',
    ],
    [
      'What do current surveys reveal about the circumgalactic medium and gas accretion in galaxies?',
      [
        'UV absorption surveys show gas halos are hot and metal-enriched',
        'The circumgalactic medium is empty and plays no role in galaxy evolution',
        'All gas in galaxies originates from the intergalactic medium with no recycling',
        'Galaxy halos are too hot to contain any neutral gas',
      ],
      0,
      'COS/HST surveys map Lyα, CIV, OVI, MgII to 200+ kpc. Hot halos with T~10⁶ K coexist with cold streams; feedback outflows (Mg II) are ubiquitous in star-forming galaxies.',
    ],
    [
      'What starburst galaxy feedback mechanisms drive the quenching of star formation?',
      [
        'Supernovae and AGN drive outflows that remove and suppress gas',
        'Starburst feedback is negligible compared to gravity in galaxy evolution',
        'All galaxies cease star formation simultaneously at a fixed cosmic time',
        'Feedback has no effect on the metallicity of galaxies',
      ],
      0,
      'SN-driven winds scale as η ~ (σ/v_wind)^α; AGN feedback dominates in massive ellipticals above M_* ~ 10¹¹ M⊙. Models require η > 1 to match observed red sequence quenching.',
    ],
    [
      'What do current constraints on the dark energy equation of state tell us about cosmological models?',
      [
        'w(a) data are consistent with Λ but allow alternative models',
        'Dark energy has been definitively proven to be a cosmological constant',
        'All dark energy models make identical observational predictions',
        'Current observations rule out all alternatives to the cosmological constant',
      ],
      0,
      'Planck + SNe + BAO + weak lensing give w ∈ [−1.08, −0.90] at 68% CL. DESI early data hint at w(z) evolution; primordial GW and early-DE models may resolve H₀ tension.',
    ],
    [
      'What do tidal disruption events reveal about supermassive black hole demographics and accretion physics?',
      [
        'A star crossing the tidal radius is shredded and forms a disk',
        'Tidal disruption events are purely theoretical with no confirmed observations',
        'All tidal disruption leads to complete black hole mergers',
        'Tidal events only occur around stellar-mass black holes',
      ],
      0,
      'X-ray transients (e.g., ASASSN-14li) show TDE signatures: thermal UV/soft X-ray, superlinear rise, flares. ATLAS/ZTF detect dozens; spectroscopy constrains spin via iron line profiles.',
    ],
    [
      'What do recent observations constrain about primordial gravitational waves and inflation models?',
      [
        'CMB B-mode polarization limits r < 0.04, constraining inflation',
        'Primordial gravitational waves have been definitively detected and inflation is proven',
        'Inflation is completely ruled out by current data',
        'No cosmological model predicts gravitational waves from the early universe',
      ],
      0,
      'Planck r limit excludes simple models (Λφ⁴); tensor modes from inflation have f ∝ k. IPTA GW signal (if confirmed) matches cosmic string or first-order PT predictions, distinct from inflation.',
    ],
    [
      'What is the physical origin and observational signature of micro-lensing by compact objects and planets?',
      [
        'Microlensing magnifies distant sources via compact lenses',
        'Microlensing is indistinguishable from other lensing phenomena',
        'Only supermassive black holes can act as microlenses',
        'Microlensing cannot detect planets',
      ],
      0,
      'Microlensing surveys (OGLE, MOA) detect planets via caustic crossings or disk effects; cold Jupiters at >2.5 AU are common. Einstein ring timescale tₑ ~ 30 d gives mass and distance simultaneously.',
    ],
    [
      'What do quasar absorption line systems and the Lyman-alpha forest reveal about feedback and reionization?',
      [
        'Damped Lyman-alpha systems and the forest constrain reionization',
        'Absorption systems contain no information about galaxy feedback',
        'Reionization was instantaneous and uniform across the universe',
        'Metal abundances in absorption systems are independent of star formation',
      ],
      0,
      '[X/H] in DLAs track star formation history and metal loss timescales (τ ~ Gyr). Forest transmitted flux near z > 6 constrains τᵣₑᵢₒₙ ~ 50–100 Myr; He II absorption shows patchy, late reionization.',
    ],
    [
      'How do extrasolar planetary system architectures (resonances, mutual inclinations, gaps) constrain formation and migration models?',
      [
        'Resonances indicate migration; eccentricities scattering',
        'Planetary system architectures are random with no connection to formation',
        'All exoplanet systems are identical in structure',
        'Migration is impossible and plays no role in system assembly',
      ],
      0,
      'TRAPPIST-1 shows resonant chain from disk migration. HR 8799 mutual i ~ 20° suggests N-body scattering post-formation. Debris disk gaps (Fomalhaut, β Pic) directly image gap-opening planets.',
    ],
  ],
};
